import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { convertHexToRgb } from 'students/views/shared/helpers';
import { useCssImageErrorProbe } from 'students/views/shared/bundles/media/useCssImageErrorProbe';

import AnimatedIcon, { IconPlaceholder } from '../AnimatedIcon';
import UrlIcon from '../UrlIcon';

interface IPhraseIcon {
  imageUrl: string | null;
  animationUrl: string | null;
  colorRequired: boolean;
  text: string;
  className?: string;
  iconClassName?: string;
  width?: string;
  height?: string;
  color?: string;
}

const PhraseIcon: FC<IPhraseIcon> = ({
  imageUrl,
  animationUrl,
  colorRequired,
  text,
  className,
  iconClassName,
  height,
  width,
  color
}) => {
  // imageUrl renders via UrlIcon's CSS mask/background, which can't fire
  // onError — probe it so task-item image failures still reach Sentry.
  useCssImageErrorProbe(imageUrl, 'phrase-icon');

  // A broken animation must not hide a good still image, so state drives what renders.
  const [animationFailed, setAnimationFailed] = useState(false);
  useEffect(() => {
    setAnimationFailed(false);
  }, [animationUrl]);

  const handleAnimationError = useCallback(() => setAnimationFailed(true), []);

  if (!animationUrl && !imageUrl && !text) return null;

  const showAnimation = Boolean(animationUrl) && !animationFailed;
  const showImage = !showAnimation && Boolean(imageUrl);
  const showText = !showAnimation && !showImage && Boolean(text);

  const renderBody = () => {
    if (showAnimation && animationUrl) {
      return (
        <SFilterWrapper $colorRequired={colorRequired} $color={color}>
          <AnimatedIcon
            animationUrl={animationUrl}
            title={text}
            height={height}
            width={width}
            className={iconClassName}
            onError={handleAnimationError}
          />
        </SFilterWrapper>
      );
    }

    if (showImage && imageUrl) {
      return (
        <SFilterWrapper $colorRequired={colorRequired} $color={color}>
          <UrlIcon
            url={imageUrl}
            title={text}
            height={height}
            width={width}
            className={iconClassName}
          />
        </SFilterWrapper>
      );
    }

    if (showText) return text;

    // Animation failed with no fallback: hold the slot so the tile does not collapse.
    return <IconPlaceholder width={width} height={height} className={iconClassName} />;
  };

  return <SWrapper className={cn(className)}>{renderBody()}</SWrapper>;
};

export default PhraseIcon;

const SWrapper = styled.div`
  text-align: center;
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

/* Solution for painting icons with filters: https://stackoverflow.com/a/62880368 */

const SFilterWrapper = styled.div<{ $colorRequired?: boolean; $color?: string }>`
  display: flex;
  justify-content: center;
  width: 100%;
  filter: ${({ $color, $colorRequired }) =>
      !$color && !$colorRequired ? 'brightness(0) invert(1)' : ''}
    drop-shadow(0 4px 0 rgba(0, 0, 0, 0.14))
    ${({ $color }) =>
      $color
        ? `
      url('data:image/svg+xml;utf8,
        <svg xmlns="http://www.w3.org/2000/svg">
          <filter id="recolor" color-interpolation-filters="sRGB">
            <feColorMatrix type="matrix" values="
              0 0 0 0 ${convertHexToRgb($color)?.r || 0}
              0 0 0 0 ${convertHexToRgb($color)?.g || 0}
              0 0 0 0 ${convertHexToRgb($color)?.b || 0}
              0 0 0 ${convertHexToRgb($color) ? 1 : 0} 0
            "/>
          </filter>
        </svg>
      #recolor')
    `
        : ''};
`;
